import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/Contacts.css';

interface Role {
  _id: string;
  name: string;
  description: string;
  permissions: string[];
  isSystem: boolean;
  workspace: string;
  createdAt: string;
  updatedAt: string;
}

// Structured permissions by module - Based on SideMenu structure
const PERMISSIONS_STRUCTURE = [
  {
    module: 'Properties',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Builders',
        permissions: [
          { key: 'VIEW_BUILDERS', label: 'View' },
          { key: 'ADD_BUILDERS', label: 'Add' },
          { key: 'EDIT_BUILDERS', label: 'Edit' },
          { key: 'DELETE_BUILDERS', label: 'Delete' }
        ]
      },
      {
        name: 'Projects',
        permissions: [
          { key: 'VIEW_PROJECTS', label: 'View' },
          { key: 'ADD_PROJECTS', label: 'Add' },
          { key: 'EDIT_PROJECTS', label: 'Edit' },
          { key: 'DELETE_PROJECTS', label: 'Delete' }
        ]
      },
      {
        name: 'Subdivisions',
        permissions: [
          { key: 'VIEW_SUBDIVISIONS', label: 'View' },
          { key: 'ADD_SUBDIVISIONS', label: 'Add' },
          { key: 'EDIT_SUBDIVISIONS', label: 'Edit' },
          { key: 'DELETE_SUBDIVISIONS', label: 'Delete' }
        ]
      },
      {
        name: 'Models',
        permissions: [
          { key: 'VIEW_MODELS', label: 'View' },
          { key: 'ADD_MODELS', label: 'Add' },
          { key: 'EDIT_MODELS', label: 'Edit' },
          { key: 'DELETE_MODELS', label: 'Delete' }
        ]
      },
      {
        name: 'Units',
        permissions: [
          { key: 'VIEW_UNITS', label: 'View' },
          { key: 'ADD_UNITS', label: 'Add' },
          { key: 'EDIT_UNITS', label: 'Edit' },
          { key: 'DELETE_UNITS', label: 'Delete' }
        ]
      },
      {
        name: 'Common Areas',
        permissions: [
          { key: 'VIEW_COMMON_AREAS', label: 'View' },
          { key: 'ADD_COMMON_AREAS', label: 'Add' },
          { key: 'EDIT_COMMON_AREAS', label: 'Edit' },
          { key: 'DELETE_COMMON_AREAS', label: 'Delete' }
        ]
      }
    ]
  },
  {
    module: 'Amenities',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Parking',
        permissions: [
          { key: 'VIEW_PARKING', label: 'View' },
          { key: 'ADD_PARKING', label: 'Add' },
          { key: 'EDIT_PARKING', label: 'Edit' },
          { key: 'DELETE_PARKING', label: 'Delete' }
        ]
      },
      {
        name: 'Lockers',
        permissions: [
          { key: 'VIEW_LOCKERS', label: 'View' },
          { key: 'ADD_LOCKERS', label: 'Add' },
          { key: 'EDIT_LOCKERS', label: 'Edit' },
          { key: 'DELETE_LOCKERS', label: 'Delete' }
        ]
      },
      {
        name: 'Bicycles',
        permissions: [
          { key: 'VIEW_BICYCLES', label: 'View' },
          { key: 'ADD_BICYCLES', label: 'Add' },
          { key: 'EDIT_BICYCLES', label: 'Edit' },
          { key: 'DELETE_BICYCLES', label: 'Delete' }
        ]
      }
    ]
  },
  {
    module: 'Package Center',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Color Collections',
        permissions: [
          { key: 'VIEW_COLOR_COLLECTIONS', label: 'View' },
          { key: 'ADD_COLOR_COLLECTIONS', label: 'Add' },
          { key: 'EDIT_COLOR_COLLECTIONS', label: 'Edit' },
          { key: 'DELETE_COLOR_COLLECTIONS', label: 'Delete' }
        ]
      },
      {
        name: 'Packages',
        permissions: [
          { key: 'VIEW_PACKAGES', label: 'View' },
          { key: 'ADD_PACKAGES', label: 'Add' },
          { key: 'EDIT_PACKAGES', label: 'Edit' },
          { key: 'DELETE_PACKAGES', label: 'Delete' }
        ]
      },
      {
        name: 'Options',
        permissions: [
          { key: 'VIEW_OPTIONS', label: 'View' },
          { key: 'ADD_OPTIONS', label: 'Add' },
          { key: 'EDIT_OPTIONS', label: 'Edit' },
          { key: 'DELETE_OPTIONS', label: 'Delete' }
        ]
      }
    ]
  },
  {
    module: 'CRM',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Contacts',
        permissions: [
          { key: 'VIEW_CONTACTS', label: 'View' },
          { key: 'ADD_CONTACTS', label: 'Add' },
          { key: 'EDIT_CONTACTS', label: 'Edit' },
          { key: 'DELETE_CONTACTS', label: 'Delete' }
        ]
      },
      {
        name: 'Deals',
        permissions: [
          { key: 'VIEW_DEALS', label: 'View' },
          { key: 'ADD_DEALS', label: 'Add' },
          { key: 'EDIT_DEALS', label: 'Edit' },
          { key: 'DELETE_DEALS', label: 'Delete' }
        ]
      }
    ]
  },
  {
    module: 'Service',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Dashboard',
        permissions: [
          { key: 'VIEW_SERVICE_DASHBOARD', label: 'View' },
          { key: 'ADD_SERVICE_DASHBOARD', label: 'Add' },
          { key: 'EDIT_SERVICE_DASHBOARD', label: 'Edit' },
          { key: 'DELETE_SERVICE_DASHBOARD', label: 'Delete' }
        ]
      },
      {
        name: 'Tarion',
        permissions: [
          { key: 'VIEW_TARION', label: 'View' },
          { key: 'ADD_TARION', label: 'Add' },
          { key: 'EDIT_TARION', label: 'Edit' },
          { key: 'DELETE_TARION', label: 'Delete' }
        ]
      },
      {
        name: 'Deficiencies',
        permissions: [
          { key: 'VIEW_DEFICIENCIES', label: 'View' },
          { key: 'ADD_DEFICIENCIES', label: 'Add' },
          { key: 'EDIT_DEFICIENCIES', label: 'Edit' },
          { key: 'DELETE_DEFICIENCIES', label: 'Delete' }
        ]
      },
      {
        name: 'Construction Status',
        permissions: [
          { key: 'VIEW_CONSTRUCTION_STATUS', label: 'View' },
          { key: 'ADD_CONSTRUCTION_STATUS', label: 'Add' },
          { key: 'EDIT_CONSTRUCTION_STATUS', label: 'Edit' },
          { key: 'DELETE_CONSTRUCTION_STATUS', label: 'Delete' }
        ]
      },
      {
        name: 'Inspection',
        permissions: [
          { key: 'VIEW_INSPECTION', label: 'View' },
          { key: 'ADD_INSPECTION', label: 'Add' },
          { key: 'EDIT_INSPECTION', label: 'Edit' },
          { key: 'DELETE_INSPECTION', label: 'Delete' }
        ]
      },
      {
        name: 'Checklist',
        permissions: [
          { key: 'VIEW_CHECKLIST', label: 'View' },
          { key: 'ADD_CHECKLIST', label: 'Add' },
          { key: 'EDIT_CHECKLIST', label: 'Edit' },
          { key: 'DELETE_CHECKLIST', label: 'Delete' }
        ]
      },
      {
        name: 'Settings',
        permissions: [
          { key: 'VIEW_SERVICE_SETTINGS', label: 'View' },
          { key: 'ADD_SERVICE_SETTINGS', label: 'Add' },
          { key: 'EDIT_SERVICE_SETTINGS', label: 'Edit' },
          { key: 'DELETE_SERVICE_SETTINGS', label: 'Delete' }
        ]
      }
    ]
  },
  {
    module: 'Security',
    submodules: [
      {
        name: 'All',
        permissions: []
      },
      {
        name: 'Users',
        permissions: [
          { key: 'VIEW_USERS', label: 'View' },
          { key: 'ADD_USERS', label: 'Add' },
          { key: 'EDIT_USERS', label: 'Edit' },
          { key: 'DELETE_USERS', label: 'Delete' }
        ]
      },
      {
        name: 'Roles',
        permissions: [
          { key: 'VIEW_ROLES', label: 'View' },
          { key: 'ADD_ROLES', label: 'Add' },
          { key: 'EDIT_ROLES', label: 'Edit' },
          { key: 'DELETE_ROLES', label: 'Delete' }
        ]
      }
    ]
  }
];

// Get all available permissions as a flat array
const getAllPermissions = () => {
  const allPerms: string[] = [];
  PERMISSIONS_STRUCTURE.forEach(module => {
    module.submodules.forEach(sub => {
      sub.permissions.forEach(p => allPerms.push(p.key));
    });
  });
  return allPerms;
};

const Roles: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  const [errors, setErrors] = useState({
    name: ''
  });

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  useEffect(() => {
    if (workspaceId) {
      fetchRoles();
    }
  }, [workspaceId]);

  const fetchRoles = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/roles`);
      setRoles(response.data.roles);
    } catch (error: any) {
      console.error('Error fetching roles:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({
      name: '',
      description: '',
      permissions: []
    });
    setErrors({ name: '' });
    setShowAddModal(true);
  };

  const handleEditClick = (role: Role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setErrors({ name: '' });
    setShowEditModal(true);
  };

  const handleDeleteClick = (role: Role) => {
    setSelectedRole(role);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
    const newErrors = { name: '' };

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    }

    setErrors(newErrors);
    return !newErrors.name;
  };

  const handleSaveAdd = async () => {
    if (!validateForm() || !workspaceId) return;

    try {
      const response = await api.post(`/workspaces/${workspaceId}/roles`, formData);
      toast.success('Role added successfully');
      setShowAddModal(false);
      // Add the new role to the list without reloading
      setRoles(prevRoles => [response.data.role, ...prevRoles]);
    } catch (error: any) {
      console.error('Error adding role:', error);
      toast.error(error.response?.data?.message || 'Failed to add role');
    }
  };

  const handleSaveEdit = async () => {
    if (!validateForm() || !selectedRole || !workspaceId) return;

    try {
      const response = await api.put(`/workspaces/${workspaceId}/roles/${selectedRole._id}`, formData);
      toast.success('Role updated successfully');
      setShowEditModal(false);
      // Update the role in the list without reloading
      setRoles(prevRoles =>
        prevRoles.map(r =>
          r._id === selectedRole._id ? response.data.role : r
        )
      );
    } catch (error: any) {
      console.error('Error updating role:', error);
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || !workspaceId) return;

    try {
      await api.delete(`/workspaces/${workspaceId}/roles/${selectedRole._id}`);
      toast.success('Role deleted successfully');
      setShowDeleteModal(false);
      // Remove the role from the list without reloading
      setRoles(prevRoles => prevRoles.filter(r => r._id !== selectedRole._id));
    } catch (error: any) {
      console.error('Error deleting role:', error);
      toast.error(error.response?.data?.message || 'Failed to delete role');
    }
  };

  const handlePermissionToggle = (permission: string) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const handleSelectAllPermissions = () => {
    const allPerms = getAllPermissions();
    if (formData.permissions.length === allPerms.length) {
      setFormData(prev => ({ ...prev, permissions: [] }));
    } else {
      setFormData(prev => ({ ...prev, permissions: [...allPerms] }));
    }
  };

  const handleModuleAllToggle = (module: typeof PERMISSIONS_STRUCTURE[0]) => {
    // Get all permissions for this module
    const modulePerms: string[] = [];
    module.submodules.forEach(sub => {
      sub.permissions.forEach(p => modulePerms.push(p.key));
    });

    // Check if all module permissions are currently selected
    const allSelected = modulePerms.every(p => formData.permissions.includes(p));

    if (allSelected) {
      // Deselect all module permissions
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !modulePerms.includes(p))
      }));
    } else {
      // Select all module permissions
      setFormData(prev => {
        const combined = [...prev.permissions, ...modulePerms];
        const unique = combined.filter((item, index) => combined.indexOf(item) === index);
        return {
          ...prev,
          permissions: unique
        };
      });
    }
  };

  const isModuleAllChecked = (module: typeof PERMISSIONS_STRUCTURE[0]) => {
    const modulePerms: string[] = [];
    module.submodules.forEach(sub => {
      sub.permissions.forEach(p => modulePerms.push(p.key));
    });
    return modulePerms.length > 0 && modulePerms.every(p => formData.permissions.includes(p));
  };

  const handleSubmoduleToggle = (submodule: typeof PERMISSIONS_STRUCTURE[0]['submodules'][0]) => {
    const subPerms: string[] = submodule.permissions.map(p => p.key);

    if (subPerms.length === 0) return; // Don't do anything for "All" row

    const allSelected = subPerms.every(p => formData.permissions.includes(p));

    if (allSelected) {
      // Deselect all submodule permissions
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !subPerms.includes(p))
      }));
    } else {
      // Select all submodule permissions
      setFormData(prev => {
        const combined = [...prev.permissions, ...subPerms];
        const unique = combined.filter((item, index) => combined.indexOf(item) === index);
        return {
          ...prev,
          permissions: unique
        };
      });
    }
  };

  const isSubmoduleChecked = (submodule: typeof PERMISSIONS_STRUCTURE[0]['submodules'][0]) => {
    const subPerms: string[] = submodule.permissions.map(p => p.key);
    // Check if at least one permission is selected
    return subPerms.length > 0 && subPerms.some(p => formData.permissions.includes(p));
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Page Header */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Roles</h1>
            <p className="builders-section-subtitle">
              Define and manage user roles and permissions
            </p>
          </div>
          <button
            onClick={handleAddClick}
            className="btn btn-primary btn-md"
          >
            <Plus size={18} />
            Add Role
          </button>
        </div>

      {loading ? (
        <div className="builders-loading">
          <div className="builders-loading-spinner" />
        </div>
      ) : (
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
        }}>
          {roles.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px dashed #D1D5DB'
            }}>
              <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '16px' }}>
                No roles found
              </p>
              <button
                onClick={handleAddClick}
                className="btn btn-primary btn-md"
              >
                <Plus size={18} />
                Add Your First Role
              </button>
            </div>
          ) : (
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Role Name</th>
                    <th>Description</th>
                    <th>Permissions</th>
                    <th style={{ width: '100px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {roles.map((role, index) => (
                    <tr
                      key={role._id}
                      style={{
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                      }}
                    >
                      <td className="data-table-primary-cell">
                        {role.name}
                        {role.isSystem && (
                          <span style={{
                            marginLeft: '8px',
                            fontSize: '11px',
                            padding: '2px 6px',
                            backgroundColor: '#DBEAFE',
                            color: '#1E40AF',
                            borderRadius: '4px',
                            fontWeight: '500'
                          }}>
                            SYSTEM
                          </span>
                        )}
                      </td>
                      <td>{role.description || '—'}</td>
                      <td>{role.permissions.length} permissions</td>
                      <td>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '8px'
                        }}>
                          <button
                            onClick={() => handleEditClick(role)}
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Edit role"
                          >
                            <Edit2 size={18} color="#48182F" strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(role)}
                            disabled={role.isSystem}
                            style={{
                              padding: '8px',
                              backgroundColor: 'transparent',
                              border: 'none',
                              cursor: role.isSystem ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: role.isSystem ? 0.4 : 1
                            }}
                            title={role.isSystem ? 'System roles cannot be deleted' : 'Delete role'}
                          >
                            <Trash2 size={18} color="#DC2626" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Role Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAddModal(false)}
        >
          <div
            className="role-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#48182F',
                margin: 0
              }}>Add New Role</h2>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="#6B7280" />
              </button>
            </div>

            <div style={{ padding: '20px 24px', flex: '1 1 auto', minHeight: 0 }}>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#48182F',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Role Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter role name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: errors.name ? '1px solid #DC2626' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#48182F',
                    boxSizing: 'border-box'
                  }}
                />
                {errors.name && (
                  <p style={{
                    color: '#DC2626',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>{errors.name}</p>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#48182F',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Description
                </label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter role description"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#48182F',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <label className="form-label" style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#48182F',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Permissions
                  </label>
                  <button
                    onClick={handleSelectAllPermissions}
                    style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: '#F3F4F6',
                      color: '#48182F',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {formData.permissions.length === getAllPermissions().length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  maxHeight: '350px',
                  overflowY: 'auto',
                  backgroundColor: 'white'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#F9FAFB',
                      borderBottom: '1px solid #E5E7EB',
                      zIndex: 1
                    }}>
                      <tr>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Modules & Permissions :</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>View</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Add</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Edit</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSIONS_STRUCTURE.map((module, idx) => (
                        <React.Fragment key={module.module}>
                          <tr style={{ backgroundColor: '#F9FAFB' }}>
                            <td colSpan={5} style={{
                              padding: '10px 16px',
                              fontWeight: '600',
                              color: '#111827',
                              borderBottom: '1px solid #E5E7EB'
                            }}>{module.module}</td>
                          </tr>
                          {module.submodules.map((sub, subIdx) => (
                            <tr key={sub.name} style={{ backgroundColor: '#FFFFFF' }}>
                              <td style={{
                                padding: '10px 16px 10px 32px',
                                color: '#374151',
                                borderBottom: subIdx === module.submodules.length - 1 && idx === PERMISSIONS_STRUCTURE.length - 1 ? 'none' : '1px solid #F3F4F6'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={sub.name === 'All' ? isModuleAllChecked(module) : isSubmoduleChecked(sub)}
                                  onChange={() => {
                                    if (sub.name === 'All') {
                                      handleModuleAllToggle(module);
                                    } else {
                                      handleSubmoduleToggle(sub);
                                    }
                                  }}
                                  style={{
                                    marginRight: '8px',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#48182F'
                                  }}
                                />
                                {sub.name}
                              </td>
                              {['View', 'Add', 'Edit', 'Delete'].map(action => {
                                const perm = sub.permissions.find(p => p.label === action);
                                return (
                                  <td key={action} style={{
                                    textAlign: 'center',
                                    padding: '10px 16px',
                                    borderBottom: subIdx === module.submodules.length - 1 && idx === PERMISSIONS_STRUCTURE.length - 1 ? 'none' : '1px solid #F3F4F6'
                                  }}>
                                    {perm && (
                                      <input
                                        type="checkbox"
                                        checked={formData.permissions.includes(perm.key)}
                                        onChange={() => handlePermissionToggle(perm.key)}
                                        style={{
                                          width: '16px',
                                          height: '16px',
                                          cursor: 'pointer',
                                          accentColor: '#48182F'
                                        }}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowAddModal(false)}
                className="btn btn-secondary"
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAdd}
                className="btn btn-primary"
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  transition: 'all 0.2s'
                }}
              >
                Add Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {showEditModal && selectedRole && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowEditModal(false)}
        >
          <div
            className="role-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '90%',
              maxWidth: '700px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#48182F',
                margin: 0
              }}>Edit Role</h2>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={20} color="#6B7280" />
              </button>
            </div>

            <div style={{ padding: '20px 24px', flex: '1 1 auto', minHeight: 0 }}>
              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#48182F',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Role Name *
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter role name"
                  disabled={selectedRole?.isSystem}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: errors.name ? '1px solid #DC2626' : '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#48182F',
                    boxSizing: 'border-box',
                    backgroundColor: selectedRole?.isSystem ? '#F3F4F6' : 'white',
                    cursor: selectedRole?.isSystem ? 'not-allowed' : 'text'
                  }}
                />
                {selectedRole?.isSystem && (
                  <p style={{
                    color: '#6B7280',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>System role names cannot be changed</p>
                )}
                {errors.name && (
                  <p style={{
                    color: '#DC2626',
                    fontSize: '12px',
                    marginTop: '4px'
                  }}>{errors.name}</p>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label className="form-label" style={{
                  display: 'block',
                  marginBottom: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#48182F',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Description
                </label>
                <textarea
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter role description"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    color: '#48182F',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <label className="form-label" style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#48182F',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Permissions
                  </label>
                  <button
                    onClick={handleSelectAllPermissions}
                    style={{
                      padding: '4px 12px',
                      fontSize: '12px',
                      backgroundColor: '#F3F4F6',
                      color: '#48182F',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {formData.permissions.length === getAllPermissions().length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  maxHeight: '350px',
                  overflowY: 'auto',
                  backgroundColor: 'white'
                }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '13px'
                  }}>
                    <thead style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: '#F9FAFB',
                      borderBottom: '1px solid #E5E7EB',
                      zIndex: 1
                    }}>
                      <tr>
                        <th style={{
                          textAlign: 'left',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Modules & Permissions :</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>View</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Add</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Edit</th>
                        <th style={{
                          textAlign: 'center',
                          padding: '12px 16px',
                          fontWeight: '600',
                          color: '#48182F',
                          width: '80px',
                          borderBottom: '1px solid #E5E7EB'
                        }}>Delete</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PERMISSIONS_STRUCTURE.map((module, idx) => (
                        <React.Fragment key={module.module}>
                          <tr style={{ backgroundColor: '#F9FAFB' }}>
                            <td colSpan={5} style={{
                              padding: '10px 16px',
                              fontWeight: '600',
                              color: '#111827',
                              borderBottom: '1px solid #E5E7EB'
                            }}>{module.module}</td>
                          </tr>
                          {module.submodules.map((sub, subIdx) => (
                            <tr key={sub.name} style={{ backgroundColor: '#FFFFFF' }}>
                              <td style={{
                                padding: '10px 16px 10px 32px',
                                color: '#374151',
                                borderBottom: subIdx === module.submodules.length - 1 && idx === PERMISSIONS_STRUCTURE.length - 1 ? 'none' : '1px solid #F3F4F6'
                              }}>
                                <input
                                  type="checkbox"
                                  checked={sub.name === 'All' ? isModuleAllChecked(module) : isSubmoduleChecked(sub)}
                                  onChange={() => {
                                    if (sub.name === 'All') {
                                      handleModuleAllToggle(module);
                                    } else {
                                      handleSubmoduleToggle(sub);
                                    }
                                  }}
                                  style={{
                                    marginRight: '8px',
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#48182F'
                                  }}
                                />
                                {sub.name}
                              </td>
                              {['View', 'Add', 'Edit', 'Delete'].map(action => {
                                const perm = sub.permissions.find(p => p.label === action);
                                return (
                                  <td key={action} style={{
                                    textAlign: 'center',
                                    padding: '10px 16px',
                                    borderBottom: subIdx === module.submodules.length - 1 && idx === PERMISSIONS_STRUCTURE.length - 1 ? 'none' : '1px solid #F3F4F6'
                                  }}>
                                    {perm && (
                                      <input
                                        type="checkbox"
                                        checked={formData.permissions.includes(perm.key)}
                                        onChange={() => handlePermissionToggle(perm.key)}
                                        style={{
                                          width: '16px',
                                          height: '16px',
                                          cursor: 'pointer',
                                          accentColor: '#48182F'
                                        }}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="btn btn-primary"
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  transition: 'all 0.2s'
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedRole && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              maxWidth: '400px',
              width: '90%',
              padding: '24px'
            }}
          >
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#48182F',
              marginBottom: '12px'
            }}>Delete Role</h2>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              marginBottom: '24px'
            }}>
              Are you sure you want to delete the role <strong>{selectedRole.name}</strong>? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  transition: 'all 0.2s'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  transition: 'all 0.2s'
                }}
              >
                Delete Role
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .role-modal::-webkit-scrollbar {
          width: 8px;
        }

        .role-modal::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 4px;
        }

        .role-modal::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 4px;
        }

        .role-modal::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }

        .permissions-grid::-webkit-scrollbar {
          width: 6px;
        }

        .permissions-grid::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 3px;
        }

        .permissions-grid::-webkit-scrollbar-thumb {
          background: #D1D5DB;
          border-radius: 3px;
        }

        .permissions-grid::-webkit-scrollbar-thumb:hover {
          background: #9CA3AF;
        }

        .btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .btn-primary:hover {
          background-color: #5a1f3a !important;
        }

        .btn-secondary:hover {
          background-color: #E5E7EB !important;
        }
      `}</style>
      </div>
    </AppLayout>
  );
};

export default Roles;
